import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { formatCurrency, formatDate, getMonthsShort } from '../utils';
import {
  IconCheck, IconLink, IconRefresh, IconAlert, IconReceipt,
  IconSearch, IconChevronRight, IconDoc,
} from '../components/Icons';
import { SkeletonCard } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import { Btn } from '../components/Input';
import { useToast } from '../components/Toast';
import HelpHint from '../components/HelpHint';
import { useConfirm } from '../components/ConfirmDialog';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TODAY = new Date().toISOString().slice(0, 10);

function daysOverdue(termin) {
  if (!termin) return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(termin); due.setHours(0, 0, 0, 0);
  return Math.floor((today - due) / 86400000);
}

function pluralPL(n, one, few, many) {
  const abs = Math.abs(n);
  const m10 = abs % 10;
  const m100 = abs % 100;
  if (abs === 1) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

function StatusPill({ status, daysOver }) {
  const { t } = useTranslation();
  if (status === 'zaplacona') {
    return (
      <span className="pill pill-ok" style={{ whiteSpace: 'nowrap' }}>
        <IconCheck className="w-3 h-3" /> {t('invoices.statusPaid')}
      </span>
    );
  }
  if (daysOver > 0) {
    return (
      <span className="pill pill-err" style={{ whiteSpace: 'nowrap' }}>
        <IconAlert className="w-3 h-3" /> {daysOver} {pluralPL(daysOver, t('common.day'), t('common.days'), t('common.days'))}
      </span>
    );
  }
  return (
    <span className="pill" style={{ background: 'var(--bg4)', color: 'var(--ink2)', whiteSpace: 'nowrap' }}>
      {t('invoices.statusIssued')}
    </span>
  );
}

// Średni czas płatności (od wystawienia do wpłaty) — w dniach
function avgPaymentDays(faktury) {
  const opF = faktury.filter(f => f.status === 'zaplacona' && f.data_wystawienia && f.data_wplaty);
  if (opF.length === 0) return null;
  const sum = opF.reduce((s, f) => {
    const ms = new Date(f.data_wplaty) - new Date(f.data_wystawienia);
    return s + Math.max(0, Math.floor(ms / 86400000));
  }, 0);
  return Math.round(sum / opF.length);
}

// ─── KPI z opcjonalnym kliknięciem ───────────────────────────────────────────

function KPI({ label, value, sub, color, onClick, hint }) {
  const clickable = !!onClick;
  const inner = (
    <>
      <p style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', marginBottom: '4px' }}>{label}</p>
      <p style={{ fontSize: '20px', fontWeight: 800, fontFamily: 'monospace', color: color || 'var(--ink)' }}>{value}</p>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        {sub && <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>{sub}</p>}
        {clickable && (
          <span style={{ color: 'var(--ink3)', flexShrink: 0, opacity: 0.6 }} title={hint}>
            <IconChevronRight className="w-3 h-3" />
          </span>
        )}
      </div>
    </>
  );
  if (!clickable) return <div className="ds-card p-4">{inner}</div>;
  return (
    <button onClick={onClick}
      className="ds-card p-4 transition-all"
      style={{ textAlign: 'left', cursor: 'pointer', width: '100%' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(184,84,40,0.4)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
      {inner}
    </button>
  );
}

// ─── Filter pill z count'em ───────────────────────────────────────────────────

function FilterPill({ label, count, active, onClick, color }) {
  return (
    <button type="button" onClick={onClick}
      className="px-3 py-1.5 transition-colors flex items-center gap-1.5"
      style={active
        ? { background: color || 'var(--acc)', color: '#fff', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', borderRadius: '7px', border: '1px solid transparent' }
        : { background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', borderRadius: '7px', border: '1px solid var(--border)' }}>
      <span>{label}</span>
      <span style={{
        fontSize: '9px', fontWeight: 700, padding: '1px 6px', borderRadius: '999px',
        background: active ? 'rgba(255,255,255,0.22)' : 'var(--bg2)',
        color: active ? '#fff' : 'var(--ink3)',
        border: active ? '1px solid rgba(255,255,255,0.18)' : '1px solid var(--border)',
        minWidth: '18px', textAlign: 'center', lineHeight: 1.4,
      }}>{count}</span>
    </button>
  );
}

// ─── Modal: szczegóły faktury (klik karta) ───────────────────────────────────

function FakturaPdfSection({ f, onChange }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);

  if (f.typ === 'pakiet' || f.legacy) return null;

  const pickFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error('Plik jest większy niż 10 MB'); return; }
      setBusy(true);
      try {
        await api.uploadFakturaPdf(f.id, file);
        toast.success('PDF dodany');
        onChange?.();
      } catch (e) { toast.error(e.message); }
      finally { setBusy(false); }
    };
    input.click();
  };

  const download = async () => {
    setBusy(true);
    try { await api.downloadFakturaPdf(f.id, f.pdf_filename); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    const ok = await confirm({ title: 'Usunąć załącznik PDF?', message: 'Klient nie będzie mógł go pobrać.', confirmText: 'Usuń', danger: true });
    if (!ok) return;
    setBusy(true);
    try {
      await api.deleteFakturaPdf(f.id);
      toast.success('Usunięto PDF');
      onChange?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
      <p style={{ fontSize: '10px', color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: '8px' }}>
        Załącznik PDF
      </p>
      {f.has_pdf ? (
        <div className="flex gap-2 flex-wrap">
          <Btn variant="ghost" onClick={download} disabled={busy}>
            <span className="flex items-center gap-1.5"><IconDoc className="w-3 h-3" /> {f.pdf_filename || 'Pobierz PDF'}</span>
          </Btn>
          <Btn variant="ghost" onClick={pickFile} disabled={busy}>Zamień</Btn>
          <Btn variant="danger" onClick={remove} disabled={busy}>Usuń</Btn>
        </div>
      ) : (
        <Btn variant="primary" onClick={pickFile} disabled={busy}>
          <span className="flex items-center gap-1.5"><IconDoc className="w-3 h-3" /> {busy ? 'Wysyłam...' : 'Dodaj PDF'}</span>
        </Btn>
      )}
      <p style={{ fontSize: '9px', color: 'var(--ink3)', marginTop: '6px' }}>
        Klient zobaczy go w swoim panelu i będzie mógł pobrać.
      </p>
    </div>
  );
}

function FakturaDetailModal({ faktura, onClose, onTogglePaid, onOpenClient, onRefresh }) {
  const { t } = useTranslation();
  if (!faktura) return null;
  const f = faktura;
  const dni = f.status !== 'zaplacona' ? daysOverdue(f.termin_platnosci) : 0;

  return (
    <Modal title={f.nr_faktury || t('invoices.noNumber')} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <span style={{
            fontSize: '8.5px', fontWeight: 700, letterSpacing: '0.06em',
            padding: '3px 8px', borderRadius: '4px',
            background: f.typ === 'pakiet' ? 'rgba(184,84,40,0.12)' : 'var(--bg4)',
            color: f.typ === 'pakiet' ? 'var(--acc)' : 'var(--ink3)',
            textTransform: 'uppercase',
          }}>
            {f.typ === 'pakiet' ? t('invoices.typePakiet') : t('invoices.typeHosting')}
          </span>
          <StatusPill status={f.status} daysOver={dni} />
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px' }} className="space-y-2">
          <Row label={t('invoices.colClient')} value={f.klient_nazwa} clickable onClick={() => { onClose(); onOpenClient(f.klient_id); }} />
          {f.typ === 'pakiet' && f.nazwa && <Row label={t('finances.packageName')} value={f.nazwa} />}
          {f.typ === 'pakiet' && f.liczba_spacerow && <Row label={t('finances.spacesCount')} value={String(f.liczba_spacerow)} />}
          {f.pozycji > 1 && <Row label={t('invoices.bulkBadge', { n: f.pozycji })} value={`${f.pozycji}`} />}
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px' }} className="space-y-2">
          <Row label={t('invoices.colIssued')} value={formatDate(f.data_wystawienia)} />
          {f.termin_platnosci && (
            <Row
              label={t('invoices.colDue')}
              value={formatDate(f.termin_platnosci)}
              valueColor={dni > 0 ? 'var(--err2)' : undefined}
              hint={dni > 0 ? t('dashboardV2.daysOverdueLong', { n: dni }) : null}
            />
          )}
          {f.data_wplaty && <Row label={t('invoices.colPaid')} value={formatDate(f.data_wplaty)} valueColor="var(--ok2)" />}
          {f.forma_platnosci && <Row label={t('clientDetail.paymentForm')} value={f.forma_platnosci} />}
          {f.notatki && <Row label={t('common.notes')} value={f.notatki} multiline />}
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
          <Row label={t('invoices.colAmount')} value={formatCurrency(f.kwota)} mono valueColor="var(--ink)" big />
        </div>

        <FakturaPdfSection f={f} onChange={onRefresh} />

        {!f.legacy && (
          <div className="flex gap-2 mt-2">
            {f.status === 'zaplacona' ? (
              <Btn variant="ghost" onClick={() => { onClose(); onTogglePaid(f, 'wystawiona'); }}>
                <span className="flex items-center gap-1.5"><IconRefresh className="w-3 h-3" /> {t('invoices.markUnpaid')}</span>
              </Btn>
            ) : (
              <Btn variant="success" onClick={() => { onClose(); onTogglePaid(f, 'zaplacona'); }}>
                <span className="flex items-center gap-1.5"><IconCheck className="w-3.5 h-3.5" /> {t('invoices.markPaid')}</span>
              </Btn>
            )}
            <Btn variant="ghost" onClick={() => { onClose(); onOpenClient(f.klient_id); }}>
              <span className="flex items-center gap-1.5"><IconLink className="w-3 h-3" /> {t('invoices.openClient')}</span>
            </Btn>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Row({ label, value, valueColor, hint, mono, multiline, big, clickable, onClick }) {
  return (
    <div className="flex justify-between gap-3" style={{ alignItems: multiline ? 'flex-start' : 'baseline' }}>
      <span style={{ fontSize: '10px', color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }} className="shrink-0">
        {label}
      </span>
      <div style={{ textAlign: 'right', minWidth: 0, flex: 1 }}>
        {clickable ? (
          <button onClick={onClick} className="hover:underline"
            style={{ fontSize: big ? '14px' : '12px', fontWeight: big ? 800 : 600, color: valueColor || 'var(--ink)', fontFamily: mono ? 'monospace' : undefined, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'right' }}>
            {value}
          </button>
        ) : (
          <span style={{ fontSize: big ? '14px' : '12px', fontWeight: big ? 800 : 600, color: valueColor || 'var(--ink)', fontFamily: mono ? 'monospace' : undefined }} className={multiline ? 'whitespace-pre-wrap' : ''}>
            {value}
          </span>
        )}
        {hint && (
          <p style={{ fontSize: '10px', color: valueColor || 'var(--err2)', marginTop: '1px' }}>{hint}</p>
        )}
      </div>
    </div>
  );
}

// ─── Modal: breakdown miesiąca ────────────────────────────────────────────────

function MonthBreakdownModal({ month, items, dzis, onClose, onClickFaktura }) {
  const { t } = useTranslation();
  if (!month) return null;

  const wystawione = items.reduce((s, f) => s + (f.kwota || 0), 0);
  const oplacone = items.filter(f => f.status === 'zaplacona').reduce((s, f) => s + (f.kwota || 0), 0);
  const nieoplacone = items.filter(f => f.status !== 'zaplacona').reduce((s, f) => s + (f.kwota || 0), 0);
  const overdue = items.filter(f => f.status !== 'zaplacona' && daysOverdue(f.termin_platnosci) > 0)
                       .reduce((s, f) => s + (f.kwota || 0), 0);

  const paidPct = wystawione > 0 ? (oplacone / wystawione) * 100 : 0;
  const overduePct = wystawione > 0 ? (overdue / wystawione) * 100 : 0;
  const unpaidNotOverduePct = Math.max(0, 100 - paidPct - overduePct);

  return (
    <Modal title={month} onClose={onClose} wide>
      <div className="flex flex-col gap-4">
        <p style={{ fontSize: '11px', color: 'var(--ink3)' }}>
          {items.length} {pluralPL(items.length, t('dashboardV2.invoice1'), t('dashboardV2.invoice2'), t('dashboardV2.invoice5'))}
        </p>

        {/* Progress bar */}
        <div>
          <div style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', background: 'var(--bg4)' }}>
            {paidPct > 0 && <div style={{ width: `${paidPct}%`, background: 'var(--ok2)' }} />}
            {unpaidNotOverduePct > 0 && <div style={{ width: `${unpaidNotOverduePct}%`, background: 'var(--acc2)' }} />}
            {overduePct > 0 && <div style={{ width: `${overduePct}%`, background: 'var(--err2)' }} />}
          </div>
          <div className="flex flex-wrap gap-3 mt-2" style={{ fontSize: '10px' }}>
            <span><span style={{ color: 'var(--ok2)', fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(oplacone)}</span> <span style={{ color: 'var(--ink3)' }}>{t('dashboardV2.legendPaid')}</span></span>
            <span><span style={{ color: 'var(--acc2)', fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(nieoplacone - overdue)}</span> <span style={{ color: 'var(--ink3)' }}>{t('dashboardV2.legendToPay')}</span></span>
            {overdue > 0 && <span><span style={{ color: 'var(--err2)', fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(overdue)}</span> <span style={{ color: 'var(--ink3)' }}>{t('dashboardV2.legendOverdue')}</span></span>}
            <span style={{ marginLeft: 'auto' }}><span style={{ color: 'var(--ink)', fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(wystawione)}</span> <span style={{ color: 'var(--ink3)' }}>{t('dashboardV2.legendTotal')}</span></span>
          </div>
        </div>

        {/* Lista faktur */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
          {items.map(f => {
            const dni = f.status !== 'zaplacona' ? daysOverdue(f.termin_platnosci) : 0;
            return (
              <button key={f.id}
                onClick={() => { onClose(); onClickFaktura(f); }}
                className="text-left w-full"
                style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', background: 'none', border: 'none', cursor: 'pointer' }}>
                <div className="flex items-center justify-between gap-2">
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ink)' }} className="truncate">
                    {f.klient_nazwa} {f.nr_faktury && <span style={{ color: 'var(--ink3)', fontWeight: 400 }}>· {f.nr_faktury}</span>}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: f.status === 'zaplacona' ? 'var(--ok2)' : (dni > 0 ? 'var(--err2)' : 'var(--acc2)') }}>
                    {formatCurrency(f.kwota)}
                  </span>
                </div>
                <div style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>
                  {f.status === 'zaplacona'
                    ? t('dashboardV2.paidOnDate', { date: formatDate(f.data_wplaty) })
                    : (f.termin_platnosci ? t('dashboardV2.termOnDate', { date: formatDate(f.termin_platnosci) }) : '—')}
                  {dni > 0 && <span style={{ color: 'var(--err2)', fontWeight: 600 }}> · {t('dashboardV2.daysOverdueLong', { n: dni })}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

// ─── Wiersz desktop (1 layout — naprawiony bug) ──────────────────────────────

function FakturaRow({ f, dni, isFirst, onTogglePaid, onCancelPayment, onOpen, onOpenClient, t }) {
  const overdue = dni > 0;
  return (
    <button
      onClick={onOpen}
      className="hidden md:grid w-full text-left"
      style={{
        gridTemplateColumns: 'minmax(140px, 1.2fr) minmax(160px, 1.4fr) 100px 110px minmax(100px, 1fr) auto',
        alignItems: 'center', gap: '12px',
        padding: '10px 16px',
        borderTop: isFirst ? 'none' : '1px solid var(--border)',
        fontSize: '11px',
        background: 'none', border: 'none', cursor: 'pointer',
      }}>
      <div style={{ minWidth: 0 }}>
        <div className="flex items-center gap-2">
          <span style={{
            fontSize: '8.5px', fontWeight: 700, letterSpacing: '0.06em',
            padding: '2px 6px', borderRadius: '4px',
            background: f.typ === 'pakiet' ? 'rgba(184,84,40,0.12)' : 'var(--bg4)',
            color: f.typ === 'pakiet' ? 'var(--acc)' : 'var(--ink3)',
            textTransform: 'uppercase', flexShrink: 0,
          }}>
            {f.typ === 'pakiet' ? t('invoices.typePakiet') : t('invoices.typeHosting')}
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--ink)' }}>
            {f.nr_faktury || <span style={{ color: 'var(--ink3)', fontStyle: 'italic', fontWeight: 400 }}>{t('invoices.noNumber')}</span>}
          </span>
        </div>
        {f.typ === 'pakiet' && f.nazwa && (
          <div style={{ fontSize: '9.5px', color: 'var(--ink3)', marginTop: '2px' }} className="truncate">{f.nazwa}</div>
        )}
        {f.typ === 'hosting' && f.pozycji > 1 && (
          <div style={{ fontSize: '9.5px', color: 'var(--ink3)', marginTop: '2px' }}>
            {t('invoices.bulkBadge', { n: f.pozycji })}
          </div>
        )}
      </div>

      <span style={{ color: 'var(--ink2)' }} className="truncate">{f.klient_nazwa}</span>

      <span style={{ color: 'var(--ink2)' }}>{formatDate(f.data_wystawienia)}</span>

      <div>
        {f.status === 'zaplacona'
          ? <span style={{ color: 'var(--ok2)' }}>{f.data_wplaty ? formatDate(f.data_wplaty) : '—'}</span>
          : f.termin_platnosci
            ? (
              <div>
                <span style={{ color: overdue ? 'var(--err2)' : 'var(--ink2)' }}>{formatDate(f.termin_platnosci)}</span>
                {overdue && (
                  <div style={{ fontSize: '9.5px', color: 'var(--err2)', marginTop: '1px' }}>
                    {t('dashboardV2.daysOverdueShort', { n: dni })}
                  </div>
                )}
              </div>
            )
            : <span style={{ color: 'var(--ink3)' }}>—</span>}
      </div>

      <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ink)', textAlign: 'right' }}>
        {formatCurrency(f.kwota)}
      </span>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
        <StatusPill status={f.status} daysOver={dni} />
        {!f.legacy && (
          f.status === 'zaplacona' ? (
            <button
              onClick={e => { e.stopPropagation(); onCancelPayment(f); }}
              title={t('invoices.markUnpaid')}
              style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px', padding: '4px 6px', cursor: 'pointer', color: 'var(--ink3)' }}>
              <IconRefresh className="w-3 h-3" />
            </button>
          ) : (
            <button
              onClick={e => { e.stopPropagation(); onTogglePaid(f, 'zaplacona'); }}
              title={t('invoices.markPaid')}
              style={{ background: 'var(--ok2)', border: 'none', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', color: '#fff', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
              <IconCheck className="w-3 h-3" /> {t('invoices.markPaid')}
            </button>
          )
        )}
        <button
          onClick={e => { e.stopPropagation(); onOpenClient(f.klient_id); }}
          title={t('invoices.openClient')}
          style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px', padding: '4px 6px', cursor: 'pointer', color: 'var(--ink3)' }}>
          <IconLink className="w-3 h-3" />
        </button>
      </div>
    </button>
  );
}

// ─── Karta mobile (1 layout) ─────────────────────────────────────────────────

function FakturaCard({ f, dni, isFirst, onTogglePaid, onCancelPayment, onOpen, onOpenClient, t }) {
  const overdue = dni > 0;
  return (
    <button
      onClick={onOpen}
      className="md:hidden flex flex-col w-full text-left"
      style={{
        padding: '12px 14px',
        borderTop: isFirst ? 'none' : '1px solid var(--border)',
        gap: '8px',
        background: 'none', border: 'none', cursor: 'pointer',
      }}>
      <div className="flex items-start justify-between gap-2">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{
              fontSize: '8.5px', fontWeight: 700, letterSpacing: '0.06em',
              padding: '2px 6px', borderRadius: '4px',
              background: f.typ === 'pakiet' ? 'rgba(184,84,40,0.12)' : 'var(--bg4)',
              color: f.typ === 'pakiet' ? 'var(--acc)' : 'var(--ink3)',
              textTransform: 'uppercase',
            }}>
              {f.typ === 'pakiet' ? t('invoices.typePakiet') : t('invoices.typeHosting')}
            </span>
            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)' }}>
              {f.nr_faktury || <span style={{ color: 'var(--ink3)', fontStyle: 'italic', fontWeight: 400 }}>{t('invoices.noNumber')}</span>}
            </span>
          </div>
          {f.typ === 'pakiet' && f.nazwa && (
            <div style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '2px' }}>{f.nazwa}</div>
          )}
        </div>
        <StatusPill status={f.status} daysOver={dni} />
      </div>

      <div style={{ fontSize: '11px', color: 'var(--ink2)' }}>{f.klient_nazwa}</div>

      <div className="flex items-center justify-between" style={{ borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
        <div style={{ fontSize: '10px', color: 'var(--ink3)' }}>
          {f.status === 'zaplacona' ? (
            <span>{t('dashboardV2.paidOnDate', { date: '' })} <span style={{ color: 'var(--ok2)' }}>{formatDate(f.data_wplaty)}</span></span>
          ) : f.termin_platnosci ? (
            <span>
              {t('dashboardV2.termOnDate', { date: '' })} <span style={{ color: overdue ? 'var(--err2)' : 'var(--ink2)' }}>{formatDate(f.termin_platnosci)}</span>
              {overdue && <span style={{ color: 'var(--err2)', marginLeft: '6px', fontWeight: 600 }}>· {t('dashboardV2.daysOverdueShort', { n: dni })}</span>}
            </span>
          ) : (
            <span>{t('dashboardV2.issuedOnDate', { date: formatDate(f.data_wystawienia) })}</span>
          )}
        </div>
        <span style={{ fontSize: '14px', fontWeight: 700, fontFamily: 'monospace', color: 'var(--ink)' }}>
          {formatCurrency(f.kwota)}
        </span>
      </div>

      {!f.legacy && (
        <div className="flex gap-2" onClick={e => e.stopPropagation()}>
          {f.status === 'zaplacona' ? (
            <button
              onClick={() => onCancelPayment(f)}
              style={{ flex: 1, background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: 'var(--ink3)', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              <IconRefresh className="w-3 h-3" /> {t('invoices.markUnpaid')}
            </button>
          ) : (
            <button
              onClick={() => onTogglePaid(f, 'zaplacona')}
              style={{ flex: 1, background: 'var(--ok2)', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: '#fff', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              <IconCheck className="w-3 h-3" /> {t('invoices.markPaid')}
            </button>
          )}
          <button
            onClick={() => onOpenClient(f.klient_id)}
            style={{ background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: 'var(--ink3)' }}
            aria-label={t('invoices.openClient')}>
            <IconLink className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </button>
  );
}

// ─── Group miesięczny — sticky header + klikalne kwoty ───────────────────────

function MonthGroup({ ym, invoices, dzis, onTogglePaid, onCancelPayment, onOpen, onOpenClient, onClickBreakdown }) {
  const { t } = useTranslation();
  const MONTHS = getMonthsShort();
  const [year, month] = ym.split('-').map(Number);
  const monthLabel = `${MONTHS[month - 1]} ${year}`;

  const wystawione = invoices.reduce((s, f) => s + (f.kwota || 0), 0);
  const oplacone = invoices.filter(f => f.status === 'zaplacona').reduce((s, f) => s + (f.kwota || 0), 0);
  const nieoplacone = invoices.filter(f => f.status !== 'zaplacona').reduce((s, f) => s + (f.kwota || 0), 0);

  return (
    <div className="ds-card" style={{ marginBottom: '14px', overflow: 'hidden' }}>
      <button
        onClick={() => onClickBreakdown(monthLabel, invoices)}
        className="w-full text-left"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '12px', flexWrap: 'wrap',
          padding: '12px 16px', borderBottom: '1px solid var(--border)',
          background: 'var(--bg2)',
          position: 'sticky', top: 0, zIndex: 5,
          cursor: 'pointer', border: 'none',
        }}>
        <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
          {monthLabel}
          <span style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 500, marginLeft: '8px' }}>
            · {invoices.length}
          </span>
        </h3>
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '10px' }}>
          <span style={{ color: 'var(--ink3)' }}>
            {t('invoices.monthIssued')}: <strong style={{ color: 'var(--ink)' }}>{formatCurrency(wystawione)}</strong>
          </span>
          <span style={{ color: 'var(--ink3)' }}>
            {t('invoices.monthPaid')}: <strong style={{ color: 'var(--ok2)' }}>{formatCurrency(oplacone)}</strong>
          </span>
          <span style={{ color: 'var(--ink3)' }}>
            {t('invoices.monthUnpaid')}: <strong style={{ color: nieoplacone > 0 ? 'var(--acc)' : 'var(--ink)' }}>{formatCurrency(nieoplacone)}</strong>
          </span>
          <span style={{ color: 'var(--ink3)' }}><IconChevronRight className="w-3 h-3 inline-block" /></span>
        </div>
      </button>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {invoices.map((f, i) => {
          const dni = f.status !== 'zaplacona' ? daysOverdue(f.termin_platnosci) : 0;
          return (
            <div key={f.id}>
              <FakturaCard
                f={f} dni={dni} isFirst={i === 0}
                onTogglePaid={onTogglePaid}
                onCancelPayment={onCancelPayment}
                onOpen={() => onOpen(f)}
                onOpenClient={onOpenClient}
                t={t}
              />
              <FakturaRow
                f={f} dni={dni} isFirst={i === 0}
                onTogglePaid={onTogglePaid}
                onCancelPayment={onCancelPayment}
                onOpen={() => onOpen(f)}
                onOpenClient={onOpenClient}
                t={t}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Główny FakturyV2 ─────────────────────────────────────────────────────────

export default function FakturyV2({ onNavigate }) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const [data, setData] = useState([]);
  const [klienci, setKlienci] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [typFilter, setTypFilter] = useState(''); // '' | 'hosting' | 'pakiet'
  const [klientFilter, setKlientFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [search, setSearch] = useState('');
  const [detailModal, setDetailModal] = useState(null);
  const [breakdownModal, setBreakdownModal] = useState(null); // { month, items } | null
  const dzis = TODAY;

  const load = useCallback(async () => {
    try {
      const [rows, kl] = await Promise.all([api.getFakturyAll(), api.getKlienci()]);
      setData(rows);
      setKlienci(kl);
    } catch (e) {
      console.error('[faktury v2] load failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Lata dostępne w danych
  const yearsAvailable = useMemo(() => {
    const set = new Set();
    data.forEach(f => {
      const y = (f.data_wystawienia || '').slice(0, 4);
      if (y) set.add(y);
    });
    return [...set].sort().reverse();
  }, [data]);

  // Filtrowanie
  const filtered = useMemo(() => {
    return data.filter(f => {
      if (filter === 'paid' && f.status !== 'zaplacona') return false;
      if (filter === 'issued' && f.status === 'zaplacona') return false;
      if (filter === 'overdue' && (f.status === 'zaplacona' || daysOverdue(f.termin_platnosci) <= 0)) return false;
      if (typFilter && f.typ !== typFilter) return false;
      if (klientFilter && String(f.klient_id) !== String(klientFilter)) return false;
      if (yearFilter && !(f.data_wystawienia || '').startsWith(yearFilter)) return false;
      if (search) {
        const q = search.toLowerCase();
        const klient = (f.klient_nazwa || '').toLowerCase();
        const nr = (f.nr_faktury || '').toLowerCase();
        const nazwa = (f.nazwa || '').toLowerCase();
        if (!klient.includes(q) && !nr.includes(q) && !nazwa.includes(q)) return false;
      }
      return true;
    });
  }, [data, filter, typFilter, klientFilter, yearFilter, search]);

  // Counts dla filter pills
  const counts = useMemo(() => {
    const c = { all: data.length, issued: 0, paid: 0, overdue: 0 };
    data.forEach(f => {
      if (f.status === 'zaplacona') c.paid += 1;
      else c.issued += 1;
      if (f.status !== 'zaplacona' && daysOverdue(f.termin_platnosci) > 0) c.overdue += 1;
    });
    return c;
  }, [data]);

  // KPI calculations (dla filtered datasetu — żeby reagowało na filtry)
  const stats = useMemo(() => {
    const wystawione = filtered.reduce((s, f) => s + (f.kwota || 0), 0);
    const dozaplaty = filtered.filter(f => f.status !== 'zaplacona').reduce((s, f) => s + (f.kwota || 0), 0);
    const przeterminowane = filtered.filter(f => f.status !== 'zaplacona' && daysOverdue(f.termin_platnosci) > 0).reduce((s, f) => s + (f.kwota || 0), 0);
    const avgDays = avgPaymentDays(filtered);
    return { wystawione, dozaplaty, przeterminowane, avgDays };
  }, [filtered]);

  // Grupowanie po YYYY-MM
  const groups = useMemo(() => {
    const map = new Map();
    for (const f of filtered) {
      const ym = (f.data_wystawienia || '').slice(0, 7) || '0000-00';
      if (!map.has(ym)) map.set(ym, []);
      map.get(ym).push(f);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  // Akcje
  const onTogglePaid = async (f, newStatus) => {
    if (f.legacy) return;
    try {
      if (f.typ === 'pakiet') {
        const pakStatus = newStatus === 'zaplacona' ? 'zaplacone' : 'niezaplacone';
        await api.setPakietStatus(f.pakiet_id, pakStatus);
      } else {
        await api.setFakturaStatus(f.id, newStatus);
      }
      setData(prev => prev.map(x => x.id === f.id
        ? { ...x, status: newStatus, data_wplaty: newStatus === 'zaplacona' ? new Date().toISOString().slice(0, 10) : null }
        : x));
      toast.success(t('toast.updated'));
    } catch (e) {
      toast.error(e.message);
      load();
    }
  };

  const onCancelPayment = async (f) => {
    const ok = await confirm({
      title: t('dashboardV2.confirmCancelPaymentTitle'),
      message: t('dashboardV2.confirmCancelPaymentMessage', { date: f.data_wplaty ? formatDate(f.data_wplaty) : '—' }),
      confirmLabel: t('dashboardV2.confirmCancelPaymentButton'),
      cancelLabel: t('common.cancel'),
      danger: true,
    });
    if (!ok) return;
    onTogglePaid(f, 'wystawiona');
  };

  const onOpenClient = (klient_id) => {
    if (onNavigate) onNavigate('klient', klient_id);
  };

  return (
    <div>
      <FakturaDetailModal
        faktura={detailModal}
        onClose={() => setDetailModal(null)}
        onTogglePaid={onTogglePaid}
        onOpenClient={onOpenClient}
        onRefresh={async () => {
          await load();
          // odśwież dane otwartego modala by pokazać nowy stan PDF
          if (detailModal) {
            const fresh = (await api.getFakturyAll()).find(x => x.id === detailModal.id);
            if (fresh) setDetailModal(fresh);
          }
        }}
      />
      {breakdownModal && (
        <MonthBreakdownModal
          month={breakdownModal.month}
          items={breakdownModal.items}
          dzis={dzis}
          onClose={() => setBreakdownModal(null)}
          onClickFaktura={f => setDetailModal(f)}
        />
      )}

      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
          {t('invoices.title')}
          <HelpHint text={t('help.invoiceStatus')} label={t('invoices.title')} />
        </h2>
        <p style={{ fontSize: '12px', color: 'var(--ink3)', margin: 0 }}>{t('invoices.subtitle')}</p>
      </div>

      {/* Stat zone */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <KPI
          label={t('dashboardV2.kpiIssued')}
          value={formatCurrency(stats.wystawione)}
          color="var(--ink)"
          sub={`${filtered.length} ${pluralPL(filtered.length, t('dashboardV2.invoice1'), t('dashboardV2.invoice2'), t('dashboardV2.invoice5'))}`}
        />
        <KPI
          label={t('dashboardV2.kpiToPay')}
          value={formatCurrency(stats.dozaplaty)}
          color={stats.dozaplaty > 0 ? 'var(--acc2)' : 'var(--ok2)'}
          sub={stats.dozaplaty > 0 ? t('dashboardV2.kpiToPaySub') : t('dashboardV2.kpiToPaySubDone')}
        />
        <KPI
          label={t('dashboardV2.kpiOverdue')}
          value={formatCurrency(stats.przeterminowane)}
          color={stats.przeterminowane > 0 ? 'var(--err2)' : 'var(--ok2)'}
          sub={stats.przeterminowane > 0 ? `${counts.overdue} ${pluralPL(counts.overdue, t('dashboardV2.invoice1'), t('dashboardV2.invoice2'), t('dashboardV2.invoice5'))}` : t('dashboardV2.kpiAccruedNone')}
          onClick={stats.przeterminowane > 0 ? () => setFilter('overdue') : undefined}
          hint={t('dashboardV2.hintFilterOverdue')}
        />
        <KPI
          label={t('dashboardV2.kpiAvgPaymentTime')}
          value={stats.avgDays != null ? `${stats.avgDays} ${pluralPL(stats.avgDays, t('common.day'), t('common.days'), t('common.days'))}` : '—'}
          color="var(--ink)"
          sub={t('dashboardV2.kpiAvgPaymentTimeSub')}
        />
      </div>

      {/* Filtry — pierwszy wiersz: status pills + typ */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <FilterPill label={t('invoices.filterAll')}      count={counts.all}     active={filter === 'all'}     onClick={() => setFilter('all')} />
        <FilterPill label={t('invoices.filterIssued')}   count={counts.issued}  active={filter === 'issued'}  onClick={() => setFilter('issued')} />
        <FilterPill label={t('invoices.filterPaid')}     count={counts.paid}    active={filter === 'paid'}    onClick={() => setFilter('paid')}    color="var(--ok2)" />
        <FilterPill label={t('invoices.filterOverdue')}  count={counts.overdue} active={filter === 'overdue'} onClick={() => setFilter('overdue')} color="var(--err2)" />

        <span style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 4px' }} />

        <FilterPill label={t('dashboardV2.filterTypeHosting')}    count={data.filter(f => f.typ === 'hosting').length}    active={typFilter === 'hosting'}    onClick={() => setTypFilter(typFilter === 'hosting' ? '' : 'hosting')} />
        <FilterPill label={t('dashboardV2.filterTypePakiet')}     count={data.filter(f => f.typ === 'pakiet').length}     active={typFilter === 'pakiet'}     onClick={() => setTypFilter(typFilter === 'pakiet' ? '' : 'pakiet')} />
      </div>

      {/* Filtry — drugi wiersz: search + klient + rok */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex-1 min-w-[200px] relative">
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--ink3)', pointerEvents: 'none' }}>
            <IconSearch className="w-3.5 h-3.5" />
          </span>
          <input
            className="ds-input"
            placeholder={t('dashboardV2.searchInvoicesPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: '32px' }}
          />
        </div>
        <select value={klientFilter} onChange={e => setKlientFilter(e.target.value)}
          className="ds-input" style={{ width: 'auto', minWidth: '160px', background: 'var(--bg2)' }}>
          <option value="">{t('dashboardV2.filterAllClients')}</option>
          {klienci.map(k => <option key={k.id} value={k.id}>{k.nazwa}</option>)}
        </select>
        {yearsAvailable.length > 1 && (
          <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
            className="ds-input" style={{ width: 'auto', background: 'var(--bg2)' }}>
            <option value="">{t('dashboardV2.filterAllYears')}</option>
            {yearsAvailable.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        {(search || klientFilter || yearFilter || typFilter) && (
          <button
            onClick={() => { setSearch(''); setKlientFilter(''); setYearFilter(''); setTypFilter(''); }}
            style={{ fontSize: '10px', color: 'var(--ink3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            {t('dashboardV2.filterClear')}
          </button>
        )}
      </div>

      {loading && (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} lines={3} />)}
        </div>
      )}

      {!loading && groups.length === 0 && (
        <EmptyState
          icon={<IconReceipt className="w-12 h-12" />}
          title={search || klientFilter || yearFilter || typFilter || filter !== 'all' ? t('dashboardV2.noFilteredInvoices') : t('invoices.noInvoices')}
          description={search || klientFilter || yearFilter || typFilter || filter !== 'all'
            ? t('dashboardV2.noFilteredHint')
            : t('invoices.noInvoicesHint')}
          action={(search || klientFilter || yearFilter || typFilter || filter !== 'all') && (
            <Btn variant="ghost" onClick={() => { setSearch(''); setKlientFilter(''); setYearFilter(''); setTypFilter(''); setFilter('all'); }}>
              {t('dashboardV2.filterClear')}
            </Btn>
          )}
        />
      )}

      {!loading && groups.map(([ym, list]) => (
        <MonthGroup
          key={ym}
          ym={ym}
          invoices={list}
          dzis={dzis}
          onTogglePaid={onTogglePaid}
          onCancelPayment={onCancelPayment}
          onOpen={f => setDetailModal(f)}
          onOpenClient={onOpenClient}
          onClickBreakdown={(month, items) => setBreakdownModal({ month, items })}
        />
      ))}
    </div>
  );
}
